import React, { useMemo, useState } from "react";
import { useConfirm, StatusBadge, PageHeader, TableSkeleton } from '../components/ui';
import { useClients, useSchedules, useAccountsByClient } from '../services/queries';
import { useNavigate } from "react-router-dom";
import { apiUtils } from "../utils/apiUtils";
import { useUser } from "../contexts/UserContext";
import SimpleModal from "../components/composite/SimpleModal";

const FREQ_LABEL = { hourly: "Hourly", daily: "Daily", weekly: "Weekly", biweekly: "Every 2 weeks", monthly: "Monthly", custom: "Custom" };
const formatHour = (h) => {
  const ampm = h < 12 ? "AM" : "PM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:00 ${ampm}`;
};

const describeSchedule = (s) => {
  const sc = s.schedule || {};
  if (sc.frequency === "hourly") return "Hourly";
  const h = Number.isInteger(sc.activeHours?.start) ? sc.activeHours.start : 9;
  const tz = (sc.timezone || "UTC").replace(/_/g, " ");
  return `${FREQ_LABEL[sc.frequency] || "Daily"} · ${formatHour(h)} · ${tz}`;
};

const fmt = (d) =>
  d ? new Date(d).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

const emptyForm = () => ({
  name: "", description: "", clientId: "", accountIds: [], frequency: "daily", runHour: 9,
  rollingDays: 7, dateRangeType: "rolling", sensitivityLevel: "medium", includeComments: false,
  enabledCategories: ["harassment", "violence", "political"], alertThreshold: 70,
});

const prefillForm = (s) => ({
  name: s.name || "",
  description: s.description || "",
  clientId: s.client?._id || s.client || "",
  accountIds: (s.scanConfig?.accounts || []).map((a) => a._id || a),
  frequency: s.schedule?.frequency || "daily",
  runHour: Number.isInteger(s.schedule?.activeHours?.start) ? s.schedule.activeHours.start : 9,
  rollingDays: s.scanConfig?.rollingDays || 7,
  dateRangeType: s.scanConfig?.dateRangeType || "rolling",
  sensitivityLevel: s.scanConfig?.analysisSettings?.sensitivityLevel || "medium",
  includeComments: !!s.scanConfig?.analysisSettings?.includeComments?.enabled,
  enabledCategories: s.scanConfig?.analysisSettings?.enabledCategories || ["harassment", "violence", "political"],
  alertThreshold: s.alerts?.thresholds?.riskScore || 70,
});

export default function MonitoringPage() {
  const confirmAction = useConfirm();
  const navigate = useNavigate();
  const { user } = useUser();
  const userTz = user?.timezone;

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("nextRun");

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);

  const { data: clients = [] } = useClients();
  const {
    data: schedules = [],
    isLoading: loading,
    refetch: refetchSchedules,
  } = useSchedules();
  // Only the selected client's accounts - the old version fetched EVERY
  // account in the workspace (plus 200 full client docs) to fill a checkbox
  // list that always filters down to one client.
  const { data: visibleAccounts = [] } = useAccountsByClient(form.clientId);

  const loadData = async () => {
    const res = await refetchSchedules();
    const list = res.data || [];
    setDetail((prev) => (prev ? list.find((x) => x._id === prev._id) || null : null));
  };

  const filtered = useMemo(() => {
    const t = (d) => (d ? new Date(d).getTime() : 0);
    let list = schedules.filter((s) => statusFilter === "all" || s.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((s) => (s.name || "").toLowerCase().includes(q) || (s.client?.name || "").toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => {
      if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
      if (sortBy === "lastRun") return t(b.schedule?.lastRunAt) - t(a.schedule?.lastRunAt);
      if (sortBy === "created") return t(b.createdAt) - t(a.createdAt);
      return t(a.schedule?.nextRunAt) - t(b.schedule?.nextRunAt);
    });
  }, [schedules, statusFilter, search, sortBy]);

  const openCreate = () => { setForm(emptyForm()); setEditing(null); setError(""); setSuccess(""); setFormOpen(true); };
  const openEdit = (s) => { setForm(prefillForm(s)); setEditing(s); setError(""); setSuccess(""); setDetail(null); setFormOpen(true); };
  const toggleAccount = (id) =>
    setForm((p) => ({ ...p, accountIds: p.accountIds.includes(id) ? p.accountIds.filter((x) => x !== id) : [...p.accountIds, id] }));

  const submitForm = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = {
        name: form.name, description: form.description, clientId: form.clientId, accountIds: form.accountIds,
        frequency: form.frequency, runHour: Number(form.runHour), activeHours: { start: Number(form.runHour) },
        dateRangeType: form.dateRangeType, rollingDays: Number(form.rollingDays),
        analysisSettings: {
          sensitivityLevel: form.sensitivityLevel,
          enabledCategories: form.enabledCategories,
          includeComments: { enabled: !!form.includeComments, limit: 10 },
        },
        alerts: { enabled: true, thresholds: { riskScore: Number(form.alertThreshold) } },
      };
      if (editing) await apiUtils.put(`/api/scheduled-scans/${editing._id}`, payload);
      else await apiUtils.post("/api/scheduled-scans", payload);
      setFormOpen(false);
      setSuccess(editing ? "Schedule updated." : "Schedule created.");
      await loadData();
    } catch (err) {
      setError(err?.response?.data?.error || err.message || "Couldn't save the schedule.");
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (id, action) => {
    if (action === "delete") {
      const ok = await confirmAction({
        title: "Delete this schedule?",
        description: "Monitoring for its accounts stops immediately. Past runs and their scans are kept.",
        confirmLabel: "Delete schedule",
        destructive: true,
      });
      if (!ok) return;
    }
    setError(""); setSuccess("");
    try {
      const method = action === "delete" ? "delete" : "post";
      const path = action === "delete" ? `/api/scheduled-scans/${id}` : `/api/scheduled-scans/${id}/${action}`;
      await apiUtils[method](path);
      setSuccess(`Schedule ${action.replace("-", " ")} completed.`);
      if (action === "delete") setDetail(null);
      await loadData();
    } catch (err) {
      setError(err.message || `Failed to ${action} schedule.`);
    }
  };

  const RowActions = ({ s }) => (
    <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
      <button type="button" onClick={() => runAction(s._id, "run-now")} className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700">Run now</button>
      {s.status === "active" ? (
        <button type="button" onClick={() => runAction(s._id, "pause")} className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600">Pause</button>
      ) : (
        <button type="button" onClick={() => runAction(s._id, "resume")} className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700">Resume</button>
      )}
      <button type="button" onClick={() => runAction(s._id, "delete")} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700">Delete</button>
    </div>
  );

  const StatusPill = ({ status }) => <StatusBadge status={status} />;

  const inputCls = "w-full rounded-lg border border-input px-3 py-2 text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30";

  return (
    <main className="p-4 sm:p-8 space-y-6 max-w-6xl mx-auto">
      <PageHeader
        className="mb-0"
        title="Monitoring"
        description="Always-on recurring scans for your highest-risk accounts."
        actions={
          <button onClick={openCreate} className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-700">
            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
            New schedule
          </button>
        }
      />

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:border-red-500/30 dark:text-red-200">{error}</div>}
      {success && <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700 dark:bg-green-500/10 dark:border-green-500/30 dark:text-green-200">{success}</div>}

      {loading ? (
        <TableSkeleton rows={4} />
      ) : schedules.length === 0 ? (
        <div className="rounded-xl border border-dashed border-input bg-card p-12 text-center">
          <h3 className="text-sm font-semibold text-foreground">No monitoring yet</h3>
          <p className="mt-1 text-sm text-muted-foreground">Create a schedule to automatically re-scan accounts and get alerted on new risk.</p>
          <button onClick={openCreate} className="mt-4 inline-flex rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-brand-700">Create your first schedule</button>
        </div>
      ) : (
        <section className="bg-card border border-border rounded-xl shadow-card">
          {/* Filters */}
          <div className="flex flex-col gap-3 p-4 border-b border-border sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <svg viewBox="0 0 24 24" className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or client" className={`${inputCls} pl-9`} />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${inputCls} sm:w-40`}>
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="error">Error</option>
              <option value="disabled">Disabled</option>
            </select>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={`${inputCls} sm:w-44`}>
              <option value="nextRun">Sort: Next run</option>
              <option value="lastRun">Sort: Last run</option>
              <option value="name">Sort: Name</option>
              <option value="created">Sort: Newest</option>
            </select>
          </div>

          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-muted-foreground">No schedules match your filters.</div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="p-4 space-y-3 md:hidden">
                {filtered.map((s) => (
                  <div key={s._id} onClick={() => setDetail(s)} className="rounded-xl border border-border p-4 cursor-pointer hover:border-input">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium text-foreground truncate">{s.name}</div>
                        <div className="text-sm text-muted-foreground truncate">{s.client?.name || "Unknown"}</div>
                      </div>
                      <StatusPill status={s.status} />
                    </div>
                    <div className="mt-2 text-xs text-muted-foreground">{describeSchedule(s)}</div>
                    <div className="mt-1 text-xs text-muted-foreground">Next run: {fmt(s.schedule?.nextRunAt)}</div>
                    <div className="mt-3"><RowActions s={s} /></div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted">
                    <tr>
                      {["Name", "Client", "Schedule", "Status", "Last run", "Next run", "Actions"].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered.map((s) => (
                      <tr key={s._id} onClick={() => setDetail(s)} className="cursor-pointer hover:bg-muted">
                        <td className="px-4 py-3 text-sm font-medium text-foreground">{s.name}</td>
                        <td className="px-4 py-3 text-sm text-foreground">{s.client?.name || "Unknown"}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{describeSchedule(s)}</td>
                        <td className="px-4 py-3"><StatusPill status={s.status} /></td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{fmt(s.schedule?.lastRunAt)}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{fmt(s.schedule?.nextRunAt)}</td>
                        <td className="px-4 py-3"><RowActions s={s} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}

      {/* Create / Edit modal */}
      <SimpleModal isOpen={formOpen} onClose={() => setFormOpen(false)} title={editing ? "Edit schedule" : "New schedule"} size="xl">
        <form onSubmit={submitForm} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Name</label>
            <input className={inputCls} value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g. Weekly check - top creators" required />
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Description <span className="text-muted-foreground">(optional)</span></label>
            <textarea className={inputCls} rows={2} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="What this monitoring is for" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Client</label>
              <select className={inputCls} value={form.clientId} onChange={(e) => setForm((p) => ({ ...p, clientId: e.target.value, accountIds: [] }))} required>
                <option value="">Select client</option>
                {clients.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Check frequency</label>
              <select className={inputCls} value={form.frequency} onChange={(e) => setForm((p) => ({ ...p, frequency: e.target.value }))}>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>

          {form.frequency !== "hourly" && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Run time</label>
              <select className={inputCls} value={form.runHour} onChange={(e) => setForm((p) => ({ ...p, runHour: Number(e.target.value) }))}>
                {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{formatHour(h)}</option>)}
              </select>
              <p className="mt-1 text-xs text-muted-foreground">Runs at this time in your timezone{userTz ? ` - ${userTz.replace(/_/g, " ")}` : ""}. Change it in Settings.</p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Accounts to monitor</label>
            <div className="rounded-lg border border-border p-3 max-h-40 overflow-y-auto space-y-2">
              {visibleAccounts.length === 0 ? (
                <div className="text-sm text-muted-foreground">Select a client to choose accounts.</div>
              ) : visibleAccounts.map((a) => (
                <label key={a._id} className="flex items-center gap-2 text-sm text-foreground">
                  <input type="checkbox" className="accent-brand-600" checked={form.accountIds.includes(a._id)} onChange={() => toggleAccount(a._id)} />
                  <span>{a.handle} ({a.platform})</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">History (days)</label>
              <input type="number" min="1" max="90" className={inputCls} value={form.rollingDays} onChange={(e) => setForm((p) => ({ ...p, rollingDays: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Sensitivity</label>
              <select className={inputCls} value={form.sensitivityLevel} onChange={(e) => setForm((p) => ({ ...p, sensitivityLevel: e.target.value }))}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Alert at</label>
              <select className={inputCls} value={form.alertThreshold} onChange={(e) => setForm((p) => ({ ...p, alertThreshold: e.target.value }))}>
                <option value="30">Low (30)</option>
                <option value="50">Medium (50)</option>
                <option value="70">High (70)</option>
                <option value="90">Critical (90)</option>
              </select>
            </div>
          </div>

          {/* Opt-in, default off. Comments are third-party data and the largest
              single addition to per-scan cost, so the trade-off is stated plainly
              rather than buried. */}
          <label className="flex items-start gap-3 rounded-lg border border-border bg-muted p-3 cursor-pointer">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-input text-brand-600 focus:ring-brand-500"
              checked={!!form.includeComments}
              onChange={(e) => setForm((p) => ({ ...p, includeComments: e.target.checked }))}
            />
            <span className="text-sm">
              <span className="font-medium text-foreground">Also analyse the top 10 comments</span>
              <span className="block text-muted-foreground">
                Detects pile-ons and backlash directed <em>at</em> this account. Comment
                authors are never stored. Uses extra credits.
              </span>
            </span>
          </label>

          <div className="flex justify-end gap-3 border-t border-border pt-4">
            <button type="button" onClick={() => setFormOpen(false)} className="rounded-lg border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted">Cancel</button>
            <button type="submit" disabled={saving || form.accountIds.length === 0} className="rounded-lg bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:bg-neutral-400">
              {saving ? "Saving…" : editing ? "Save changes" : "Create schedule"}
            </button>
          </div>
        </form>
      </SimpleModal>

      {/* Detail + history modal */}
      <SimpleModal isOpen={!!detail} onClose={() => setDetail(null)} title={detail?.name || "Schedule"} size="xl">
        {detail && (
          <div className="space-y-5">
            <div className="flex items-center gap-3">
              <StatusPill status={detail.status} />
              <span className="text-sm text-muted-foreground">{describeSchedule(detail)}</span>
            </div>
            {detail.description && <p className="text-sm text-muted-foreground">{detail.description}</p>}

            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <div><dt className="text-muted-foreground text-xs">Client</dt><dd className="text-foreground">{detail.client?.name || "Unknown"}</dd></div>
              <div><dt className="text-muted-foreground text-xs">Accounts</dt><dd className="text-foreground">{(detail.scanConfig?.accounts || []).map((a) => a.handle).join(", ") || "-"}</dd></div>
              <div><dt className="text-muted-foreground text-xs">History window</dt><dd className="text-foreground">{detail.scanConfig?.rollingDays || 7} days</dd></div>
              <div><dt className="text-muted-foreground text-xs">Sensitivity</dt><dd className="text-foreground capitalize">{detail.scanConfig?.analysisSettings?.sensitivityLevel || "medium"}</dd></div>
              <div><dt className="text-muted-foreground text-xs">Alert threshold</dt><dd className="text-foreground">{detail.alerts?.thresholds?.riskScore ?? 70}</dd></div>
              <div><dt className="text-muted-foreground text-xs">Last run</dt><dd className="text-foreground">{fmt(detail.schedule?.lastRunAt)}</dd></div>
              <div><dt className="text-muted-foreground text-xs">Next run</dt><dd className="text-foreground">{fmt(detail.schedule?.nextRunAt)}</dd></div>
            </dl>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold text-foreground">Scan history</h4>
                <span className="text-xs text-muted-foreground">{(detail.executions || []).length} runs</span>
              </div>
              {(detail.executions || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No runs yet.</p>
              ) : (
                <div className="rounded-lg border border-border divide-y divide-border max-h-60 overflow-y-auto">
                  {[...detail.executions].reverse().slice(0, 15).map((ex, i) => (
                    <div key={ex._id || i} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
                      <div className="min-w-0">
                        <div className="text-foreground">{fmt(ex.executedAt)}</div>
                        <div className="text-xs text-muted-foreground">
                          {ex.stats?.accountsScanned ?? "?"} account{ex.stats?.accountsScanned === 1 ? "" : "s"}
                          {typeof ex.stats?.flaggedPosts === "number" ? ` · ${ex.stats.flaggedPosts} flagged` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${ex.status === "success" ? "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-300" : ex.status === "failed" ? "bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-300" : "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"}`}>{ex.status}</span>
                        {ex.scanId && (
                          <button onClick={() => navigate(`/scans/${ex.scanId}`)} className="text-xs font-medium text-brand-600 hover:text-brand-700">View</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
              <RowActions s={detail} />
              <button onClick={() => openEdit(detail)} className="rounded-lg border border-input px-4 py-2 text-sm font-medium text-foreground hover:bg-muted">Edit</button>
            </div>
          </div>
        )}
      </SimpleModal>
    </main>
  );
}
